/**
 * The sample sessions pages carry (search estate plan, decision 7): an excerpt of a real Quies
 * session per page, keyed by the page's path without its slash. `session-samples.json` is written
 * by `scripts/quies-sample.ts`; the audio is public under the bucket's `podcast/` prefix, the one
 * Quies audio is already served from.
 */
import samples from './session-samples.json'

export interface SessionSample {
    /** The meditation's own title, as the agent wrote it. */
    title: string
    /** Who the session was written for, in one sentence. */
    description: string
    /** BCP 47, as the page's `lang` writes it: the Polly locale, `cmn` written `zh`. */
    inLanguage: string
    voice: string
    durationSeconds: number
    contentUrl: string
    /** YYYY-MM-DD */
    uploadDate: string
    /** The excerpt as spoken, one entry per paragraph, without SSML. */
    transcript: string[]
}

export const SAMPLE_AUDIO_BASE_URL = 'https://narcis-app-eu-central-1-d931.s3.eu-central-1.amazonaws.com/podcast/samples'
export const SAMPLE_MIN_SECONDS = 120
export const SAMPLE_MAX_SECONDS = 240

export const SESSION_SAMPLES: Record<string, SessionSample> = samples

export function sampleFor(page: string): SessionSample {
    const sample = SESSION_SAMPLES[page]
    if (!sample) throw new Error(`no session sample for ${page}`)
    return sample
}

const CJK = '[\\u3000-\\u30ff\\u4e00-\\u9fff\\uff00-\\uffef]'
const SPACE_BETWEEN_CJK = new RegExp(`(?<=${CJK}) (?=${CJK})`, 'g')

/** Spoken text as read: the `<break>` tags gone, whitespace they leave collapsed — and dropped
 *  between Chinese or Japanese characters, which are written without spaces. */
export function transcriptOf(spoken: string): string {
    return spoken.replace(/<break\b[^>]*\/>/g, ' ').replace(/\s+/g, ' ').trim().replace(SPACE_BETWEEN_CJK, '')
}

/** Polly names Mandarin by its ISO 639-3 code; a page, a screen reader and schema.org know `zh`. */
export function webLanguageTag(pollyLocale: string): string {
    return pollyLocale.replace(/^cmn-/, 'zh-')
}

/** ISO 8601, for schema.org: PT3M7S. */
export function isoDuration(seconds: number): string {
    return `PT${Math.floor(seconds / 60)}M${seconds % 60}S`
}

/** 3:07 */
export function displayDuration(seconds: number): string {
    return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`
}
